/* RESPONSIBLE TEAM: team-inbox-management */
import { ParentAction, ChildAction } from './action';
import { inject as service } from '@ember/service';
import type InboxHotkeys from 'embercom/services/inbox-hotkeys';
import { HotkeyID } from 'embercom/services/inbox-hotkeys/HotkeyID';
import {
  priorityLevelOptions,
  type PriorityLevelDisplay,
  type PriorityLevelName,
} from 'embercom/lib/inbox/priority-levels';

// Static i18n keys per level — the ember skill forbids template-literal key
// construction (`...${name}`), so we look up by the closed `PriorityLevelName`
// union instead.
const LEVEL_LABEL_KEYS: Record<PriorityLevelName, string> = {
  none: 'inbox.command-k.set-priority-level.none',
  low: 'inbox.command-k.set-priority-level.low',
  medium: 'inbox.command-k.set-priority-level.medium',
  high: 'inbox.command-k.set-priority-level.high',
  urgent: 'inbox.command-k.set-priority-level.urgent',
};

// Command+K ParentAction for setting a multi-level priority. Mirrors the
// `SnoozeAction` / `AssignToAction` pattern: the action is registered globally,
// click and `P` shortcut both route through `commandK.handleShortcutFor` →
// `selectAction(parent)` which opens the pane defined by `paneComponent`.
//
// Children are derived from the static priority levels list. Selection emits
// the chosen `value` (the integer level) to the registered `onSelect` handler.
export default class SetPriorityLevelAction extends ParentAction {
  @service declare inboxHotkeys: InboxHotkeys;

  // The default id matches the per-conversation registration; the bulk
  // variant overrides via constructor args (`id: 'bulk-set-priority-level'`).
  // Both share the same pane and option components.
  id = 'set-priority-level';
  icon = 'high-priority' as const;
  paneComponent = 'inbox2/command-k/set-priority-level/pane';

  constructor(args: { owner: unknown; id?: string }) {
    super(args);
    if (args.id) {
      this.id = args.id;
    }
    this.shortcuts = this.inboxHotkeys.hotkeysMap[HotkeyID.ChangePriority];
  }

  buildTopLevelItems(query: string): Array<ChildAction> {
    return this.buildResults(query);
  }

  buildResults(query: string): Array<ChildAction> {
    let upperQuery = query.trim().toLocaleUpperCase();
    return priorityLevelOptions()
      .map((option) => this.childForOption(option))
      .filter((child) => child.label.toLocaleUpperCase().includes(upperQuery));
  }

  private childForOption(option: PriorityLevelDisplay): ChildAction {
    return new ChildAction({
      parent: this,
      id: `priority-${option.name}`,
      label: this.intl.t(LEVEL_LABEL_KEYS[option.name]),
      value: option.value,
      icon: option.icon,
      optionComponent: 'inbox2/command-k/set-priority-level/option',
    });
  }
}
